import React from "react";
import { useSelector } from "react-redux";
import { LanguageEnum } from "jadro-pico/enums/common/LanguageEnum";
import { languageToLocale } from "jadro-pico/utils/languageToLocale";
import { PICO_TIMEZONE } from "jadro-pico/constants/timezone";
import type { QueueTransactionCommandEnum } from "jadro-pico/enums/common/QueueTransactionCommandEnum";
import ListItem from "../List/ListItem/ListItem";
import ListItemLeadingElement from "../List/ListItem/ListItemLeadingElement";
import Icon from "../Icon/Icon";
import { ThemeColorEnum, ThemeIconEnum } from "../Themes/ThemeEnums";
import { useResource } from "../../../Resources/useResource";
import { RequestQueueResources } from "../../../Resources/RequestQueueResources";
import { RequestQueueStateEnum, type RequestQueueItem } from "../../../Offline/requestQueueItems";

const stateIcon: Record<RequestQueueStateEnum, ThemeIconEnum> = {
  [RequestQueueStateEnum.WAITING_TO_SEND]: ThemeIconEnum.CLOUD_OFF,
  [RequestQueueStateEnum.SENDING]: ThemeIconEnum.SYNC,
  [RequestQueueStateEnum.AWAITING_WRITE]: ThemeIconEnum.SCHEDULE,
  [RequestQueueStateEnum.NOT_SENT]: ThemeIconEnum.WARNING,
  [RequestQueueStateEnum.NOT_STORED]: ThemeIconEnum.ERROR,
  [RequestQueueStateEnum.STORED]: ThemeIconEnum.CHECK,
};

const stateColor: Record<RequestQueueStateEnum, ThemeColorEnum> = {
  [RequestQueueStateEnum.WAITING_TO_SEND]: ThemeColorEnum.ON_SURFACE_VARIANT,
  [RequestQueueStateEnum.SENDING]: ThemeColorEnum.ON_SURFACE_VARIANT,
  [RequestQueueStateEnum.AWAITING_WRITE]: ThemeColorEnum.ON_SURFACE_VARIANT,
  [RequestQueueStateEnum.NOT_SENT]: ThemeColorEnum.ERROR,
  [RequestQueueStateEnum.NOT_STORED]: ThemeColorEnum.ERROR,
  [RequestQueueStateEnum.STORED]: ThemeColorEnum.PRIMARY,
};

export function useRequestQueueRow() {
  const resources = useResource(RequestQueueResources);
  const language = useSelector(
    (s: { core: { language?: LanguageEnum } }) => s.core.language ?? LanguageEnum.CS,
  );
  const formatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(languageToLocale(language), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: PICO_TIMEZONE,
      }),
    [language],
  );
  const commandLabels: Partial<Record<QueueTransactionCommandEnum, string>> = resources.command;

  return (item: RequestQueueItem): React.ReactElement => (
    <ListItem
      key={item.operationUuid}
      headline={
        item.label ?? (item.command && commandLabels[item.command]) ?? resources.unknownLabel
      }
      supportingText={`${resources.state[item.state]} · ${formatter.format(item.at)}`}
      leadingElement={
        <ListItemLeadingElement>
          <Icon icon={stateIcon[item.state]} color={stateColor[item.state]} />
        </ListItemLeadingElement>
      }
    />
  );
}

export function isResolved(item: RequestQueueItem): boolean {
  return (
    item.state === RequestQueueStateEnum.STORED || item.state === RequestQueueStateEnum.NOT_STORED
  );
}
